// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: how the phone mail list reacts to gestures, all tunable in
// Settings > Mail: what a full swipe does each way, the short-swipe trays,
// how far a swipe must go, haptics, the undo window, delete confirmation,
// the sender tidy-up card and the one-time swipe hint.

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {makeAutoObservable} from 'mobx';

export type MailSwipeAction =
	| 'toggle_read'
	| 'flag'
	| 'reply'
	| 'archive'
	| 'delete'
	| 'remind'
	| 'task'
	| 'move'
	| 'pin'
	| 'none';
export type MailSwipeDirection = 'left' | 'right';
export type MailSwipeDistance = 'short' | 'medium' | 'long';

export const MAIL_SWIPE_ACTIONS: ReadonlyArray<MailSwipeAction> = [
	'toggle_read',
	'flag',
	'reply',
	'archive',
	'delete',
	'remind',
	'task',
	'move',
	'pin',
	'none',
];
/** Actions that fit a short-swipe tray (everything but None). */
export const MAIL_TRAY_ACTIONS: ReadonlyArray<MailSwipeAction> = MAIL_SWIPE_ACTIONS.filter((a) => a !== 'none');
export const MAIL_TRAY_MAX = 3;
export const MAIL_UNDO_CHOICES: ReadonlyArray<number> = [0, 3, 5, 8];
export const MAIL_CLEANUP_THRESHOLDS: ReadonlyArray<number> = [3, 4, 5];

/** Share of the row width a full swipe must cover. */
export const MAIL_SWIPE_DISTANCE_RATIO: Record<MailSwipeDistance, number> = {short: 0.22, medium: 0.3, long: 0.42};

/** Stored choices from before read became a toggle ('mark_read', 'mark_unread') read as the toggle. */
export function normalizeMailSwipeAction(value: unknown): MailSwipeAction {
	return MAIL_SWIPE_ACTIONS.includes(value as MailSwipeAction) ? (value as MailSwipeAction) : 'toggle_read';
}

function normalizeTray(value: unknown): Array<MailSwipeAction> {
	if (!Array.isArray(value)) return [];
	const seen = new Set<MailSwipeAction>();
	for (const entry of value) {
		if (MAIL_TRAY_ACTIONS.includes(entry as MailSwipeAction)) seen.add(entry as MailSwipeAction);
	}
	return [...seen].slice(0, MAIL_TRAY_MAX);
}

class MailSwipePreferences {
	leftAction: MailSwipeAction = 'reply';
	rightAction: MailSwipeAction = 'toggle_read';
	/** A short swipe rests open on a tray of these instead of springing back. */
	trayEnabled = true;
	leftTray: Array<MailSwipeAction> = ['flag', 'remind'];
	rightTray: Array<MailSwipeAction> = [];
	distance: MailSwipeDistance = 'medium';
	haptics = true;
	/** Seconds an archive or delete waits before it reaches Outlook; 0 moves at once. */
	undoSeconds = 5;
	/** Ask before a swipe deletes (off by default: the undo covers it, as in Outlook). */
	confirmDelete = false;
	senderCleanup = true;
	cleanupThreshold = 3;
	/** Sender address -> newest mail time when its tidy-up card was dismissed. */
	dismissedCleanups: Record<string, string> = {};
	hintShown = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void makePersistent(
			this,
			'MailSwipePreferences',
			[
				'leftAction',
				'rightAction',
				'trayEnabled',
				'leftTray',
				'rightTray',
				'distance',
				'haptics',
				'undoSeconds',
				'confirmDelete',
				'senderCleanup',
				'cleanupThreshold',
				'dismissedCleanups',
				'hintShown',
			],
			{syncAcrossTabs: true},
		);
	}

	get left(): MailSwipeAction {
		return normalizeMailSwipeAction(this.leftAction);
	}

	get right(): MailSwipeAction {
		return normalizeMailSwipeAction(this.rightAction);
	}

	actionFor(direction: MailSwipeDirection): MailSwipeAction {
		return direction === 'left' ? this.left : this.right;
	}

	/** Tray revealed by a short swipe that way; empty when trays are off. */
	trayFor(direction: MailSwipeDirection): Array<MailSwipeAction> {
		if (!this.trayEnabled) return [];
		const full = this.actionFor(direction);
		return normalizeTray(direction === 'left' ? this.leftTray : this.rightTray).filter((action) => action !== full);
	}

	get distanceRatio(): number {
		return MAIL_SWIPE_DISTANCE_RATIO[this.distance] ?? MAIL_SWIPE_DISTANCE_RATIO.medium;
	}

	get undoMs(): number {
		return MAIL_UNDO_CHOICES.includes(this.undoSeconds) ? this.undoSeconds * 1000 : 5000;
	}

	setAction(direction: MailSwipeDirection, action: MailSwipeAction): void {
		if (direction === 'left') this.leftAction = action;
		else this.rightAction = action;
	}

	toggleTrayAction(direction: MailSwipeDirection, action: MailSwipeAction): void {
		const current = normalizeTray(direction === 'left' ? this.leftTray : this.rightTray);
		const next = current.includes(action)
			? current.filter((entry) => entry !== action)
			: [...current, action].slice(-MAIL_TRAY_MAX);
		if (direction === 'left') this.leftTray = next;
		else this.rightTray = next;
	}

	setTrayEnabled(enabled: boolean): void {
		this.trayEnabled = enabled;
	}

	setDistance(distance: MailSwipeDistance): void {
		this.distance = distance;
	}

	setHaptics(enabled: boolean): void {
		this.haptics = enabled;
	}

	setUndoSeconds(seconds: number): void {
		this.undoSeconds = MAIL_UNDO_CHOICES.includes(seconds) ? seconds : 5;
	}

	setConfirmDelete(enabled: boolean): void {
		this.confirmDelete = enabled;
	}

	setSenderCleanup(enabled: boolean): void {
		this.senderCleanup = enabled;
	}

	setCleanupThreshold(count: number): void {
		this.cleanupThreshold = MAIL_CLEANUP_THRESHOLDS.includes(count) ? count : 3;
	}

	dismissCleanup(address: string, newestAt: string): void {
		this.dismissedCleanups = {...this.dismissedCleanups, [address.toLowerCase()]: newestAt};
	}

	markHintShown(): void {
		this.hintShown = true;
	}

	resetHint(): void {
		this.hintShown = false;
	}
}

export default new MailSwipePreferences();
